import { Combobox as Combo, Transition } from "@headlessui/react";
import classNames from "classnames";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { ActiveRadioIcon, InactiveRadioIcon } from "./icons/RadioButton";

export type OptionObj = { label: string; value: string; [key: string]: any };
export type OptionType = string | OptionObj;

type SelectedType<M extends boolean> = M extends true
  ? OptionType[]
  : OptionType;

type ComboboxProps<M extends boolean> = {
  multiple: M;
  options: OptionType[];
  onChange?: (selected: SelectedType<M>) => void;
  defaultSelected?: SelectedType<M>;
  placeholder?: string;
  className?: string;
  inputFieldClassName?: string;
  key?: string;
  size?: "sm" | "md";
  getOptionLabel?: (option: OptionType) => string | React.ReactNode;
  getSelectedDisplayValue?: (selection: SelectedType<M>) => string;
  selectedOptions?: SelectedType<M>;
};

function isString(x: any) {
  return typeof x === "string";
}

const getOptionDisplayValue = (option: OptionType) =>
  isString(option) ? (option as string) : (option as OptionObj).label;

// display the value for single option or comma-separated values for multiple options
const defaultSelectedDisplayValue = <M extends boolean>(
  selected: SelectedType<M>,
  multiple: M
) => {
  if (multiple) {
    return (selected as OptionType[]).map(getOptionDisplayValue).join(", ");
  }
  return getOptionDisplayValue(selected as OptionType);
};

const SelectedIcon = ({
  multiple,
  selected,
}: {
  multiple: boolean;
  selected: boolean;
}) => {
  return (
    <span>
      {multiple ? (
        selected && <Check size={20} className="text-accent-fg-base" />
      ) : selected ? (
        <ActiveRadioIcon />
      ) : (
        <InactiveRadioIcon />
      )}
    </span>
  );
};

export const Combobox = <M extends boolean>({
  options,
  multiple = false as M,
  placeholder,
  className,
  inputFieldClassName,
  onChange,
  defaultSelected,
  key,
  size = "md",
  selectedOptions,
  getOptionLabel,
  getSelectedDisplayValue,
}: ComboboxProps<M>) => {
  type CurrentSelectedType = SelectedType<M>;

  const [selected, setSelected] = useState<CurrentSelectedType>(() => {
    if (multiple) {
      return (
        (defaultSelected as CurrentSelectedType) ??
        ([] as OptionType[] as CurrentSelectedType)
      );
    }
    return (defaultSelected ?? options[0]) as CurrentSelectedType;
  });

  const handleSelect = (item: CurrentSelectedType) => {
    setSelected(item);
    onChange?.(item);
  };

  // Aligns a given selection with the current `options` array so that
  // every non-string object uses the instance that lives inside `options`.
  const reconcileSelection = (
    source: CurrentSelectedType | undefined
  ): CurrentSelectedType | undefined => {
    if (!source) return source;

    if (multiple) {
      if (!Array.isArray(source)) return source;
      return source.map((sel) => {
        if (isString(sel)) return sel;
        const match = options.find(
          (opt) =>
            !isString(opt) &&
            (opt as OptionObj).value === (sel as OptionObj).value
        );
        return (match ?? sel) as OptionType;
      }) as CurrentSelectedType;
    }

    if (isString(source)) return source;
    const objSel = source as OptionObj;
    const match = options.find(
      (opt) => !isString(opt) && (opt as OptionObj).value === objSel.value
    );
    return (match ?? objSel) as CurrentSelectedType;
  };

  // Reconcile internal selection whenever the available options change.
  useEffect(() => {
    const reconciled = reconcileSelection(selected);
    if (reconciled && reconciled !== selected) {
      setSelected(reconciled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, multiple]);

  // Reconcile incoming external selection.
  useEffect(() => {
    if (!selectedOptions) return;
    const reconciled = reconcileSelection(selectedOptions);
    if (reconciled) {
      setSelected(reconciled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions]);

  const getLabel = (option: OptionType) =>
    getOptionLabel ? getOptionLabel(option) : getOptionDisplayValue(option);

  return (
    <Combo
      value={selected}
      onChange={handleSelect}
      key={key}
      multiple={multiple}
    >
      {({ open }) => (
        <div className={classNames("relative", className)}>
          <div className="relative">
            <Combo.Input
              className={classNames(
                "border border-border-base px-3 py-2 w-full rounded-xl sm-normal",
                size === "sm" && "h-10",
                size === "md" && "h-[50px]",
                inputFieldClassName
              )}
              displayValue={(selected: CurrentSelectedType) =>
                getSelectedDisplayValue?.(selected) ??
                defaultSelectedDisplayValue(selected, multiple)
              }
              onChange={() => {}} // Prevent default onChange
              readOnly={true}
              placeholder={placeholder}
              multiple={multiple}
            />
            <Combo.Button className="absolute bg-transparent left-0 right-0 h-full">
              <div className="flex justify-end px-3">
                {open ? (
                  <ChevronUp size={20} className="text-fg-muted" />
                ) : (
                  <ChevronDown size={20} className="text-fg-muted" />
                )}
              </div>
            </Combo.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combo.Options
              className={classNames(
                "absolute mt-1 max-h-40 w-full overflow-auto p-1 rounded-xl bg-bg-base py-1 ring-1 ring-black/5 focus:outline-hidden sm:text-sm z-50 scrollbar-hide border border-border-base co-bottom-shadow"
              )}
            >
              {options.map((option) => (
                <Combo.Option
                  key={getOptionDisplayValue(option)}
                  value={option}
                >
                  {({ active, selected }) => (
                    <div
                      className={classNames(
                        "px-2 flex justify-between items-center tiny-md hover:bg-bg-faint cursor-pointer h-9 rounded-lg",
                        { "bg-bg-faint": active }
                      )}
                    >
                      <div>
                        <span>{getLabel(option)}</span>
                      </div>
                      <SelectedIcon multiple={multiple} selected={selected} />
                    </div>
                  )}
                </Combo.Option>
              ))}
            </Combo.Options>
          </Transition>
        </div>
      )}
    </Combo>
  );
};
